import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Badge } from "../components/Badge";
import type { BadgeKind } from "../components/Badge";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { DealEditor } from "./DealEditor";
import { SubmitDeal } from "./SubmitDeal";
import { formatInstantDate, formatMoney, formatPercentOff } from "../lib/format";
import { joinFieldLabels } from "../lib/deal-fields";
import { displayHost, safeExternalUrl } from "../lib/url";
import styles from "./DealsPanel.module.css";
import type { DealEditorProps } from "./DealEditor";
import type { SubmitDealProps } from "./SubmitDeal";
import type {
  SavedDeal,
  SellerDeal,
  SellerDealStatus,
  SellerDecision,
  SubmittedDeal,
} from "../api/types";

/**
 * The seller's own deals, at every review stage.
 *
 * Canon § 14 makes this the place a founder watches their deal move through review, and
 * `contracts/API.md` lists deals at every stage rather than only approved ones for the same
 * reason: hiding a deal that is still `submitted` would make the desk look empty instead of
 * honest.
 *
 * Two things are load-bearing here.
 *
 * **The review state is stated, not implied.** Each deal carries the word core sent, a
 * differently-shaped mark, and one sentence saying what that state means for the deal's
 * exposure and its ability to bid. Canon § 2/§ 3 gate both on approval, so "in review" and
 * "approved" are materially different days for a founder and must not be told apart by
 * colour (`contracts/DESIGN-TOKENS.md` § Accessibility).
 *
 * **The deal is drawn as its buyers will see it** — same badges, same price treatment as
 * `dealbid-web`'s board entry — so a founder reviewing their own submission is checking the
 * thing that will actually be published.
 */

/** Canon § 4 badge codes to the design system's badge kinds. */
const BADGE_KIND: Record<string, BadgeKind> = {
  founding: "founding",
  ltd: "ltd",
  exclusive: "exclusive",
};

interface StatusCopy {
  label: string;
  tone: StatusTone;
  /** What this state means for exposure and bidding, in one sentence. */
  meaning: string;
}

/**
 * Every state core can send, worded from the founder's side.
 *
 * `changes_required` and `rejected` share a tone and are told apart by their words, because
 * one is a request and the other is a decision.
 *
 * These sentences used to end by saying Dealbid "will be in touch with the detail" — a
 * promise nothing kept, because canon then recorded the note on the audit event and had an
 * operator relay it out of band. Canon decision `D-016` withdrew that: the note is on this
 * screen now, in {@link ReviewRequest} directly below the sentence that names the state, so
 * the copy stops pointing at a channel and the founder simply reads what was said.
 */
const STATUS_COPY: Record<SellerDealStatus, StatusCopy> = {
  submitted: {
    label: "In review",
    tone: "neutral",
    meaning:
      "Dealbid is checking the product, your association with it and the deal terms. It is not on the public board and cannot bid yet.",
  },
  changes_required: {
    label: "Changes requested",
    tone: "warning",
    meaning:
      "Dealbid asked for changes before this deal can be approved. It is not on the public board and cannot bid yet.",
  },
  approved: {
    label: "Approved",
    tone: "positive",
    meaning:
      "This deal is live on Dealbid and can bid today for a place in tomorrow's Top 10.",
  },
  rejected: {
    label: "Not approved",
    tone: "warning",
    meaning:
      "Dealbid did not approve this deal. It is not on the public board and cannot bid.",
  },
  disabled: {
    label: "Disabled",
    tone: "warning",
    meaning:
      "Dealbid has taken this deal off the board. It cannot bid while it is disabled.",
  },
};

function DealBadges({ deal }: { deal: SellerDeal }) {
  const kinds = deal.badges.flatMap((code) => {
    const kind = BADGE_KIND[code];
    return kind === undefined ? [] : [kind];
  });
  if (kinds.length === 0 && !deal.verified) return null;

  return (
    <ul className={styles.badges} role="list">
      {kinds.map((kind) => (
        <li key={kind}>
          <Badge kind={kind} />
        </li>
      ))}
      {deal.verified ? (
        <li>
          <Badge kind="verified" />
        </li>
      ) : null}
    </ul>
  );
}

function DealValue({ deal }: { deal: SellerDeal }) {
  const hasPrices = deal.originalPriceCents !== null && deal.dealPriceCents !== null;
  if (!hasPrices && deal.valueNotes === null) return null;

  return (
    <div className={styles.value}>
      {hasPrices ? (
        <p className={styles.priceRow}>
          <span className={`${styles.now} figure`}>
            <VisuallyHidden>Deal price </VisuallyHidden>
            {formatMoney(deal.dealPriceCents ?? 0)}
          </span>
          <span className={styles.was}>
            <VisuallyHidden>Usually </VisuallyHidden>
            <s className="figure">{formatMoney(deal.originalPriceCents ?? 0)}</s>
          </span>
          {deal.discountPercent === null ? null : (
            <span className={styles.off}>{formatPercentOff(deal.discountPercent)}</span>
          )}
        </p>
      ) : null}
      {/* Canon § 2: a deal whose value is not a percentage carries it in words instead. */}
      {deal.valueNotes === null ? null : <p className={styles.notes}>{deal.valueNotes}</p>}
    </div>
  );
}

/** A review timestamp, omitted entirely when core did not send one. */
function ReviewFact({ label, iso }: { label: string; iso: string | null }) {
  const date = formatInstantDate(iso);
  if (date === null) return null;
  return (
    <div className={styles.fact}>
      <dt>{label}</dt>
      <dd className="figure">{date}</dd>
    </div>
  );
}

/**
 * The heading over an outstanding note, per status.
 *
 * The words carry the meaning rather than the colour, which is what keeps this legible to a
 * founder who cannot tell the warning rule from the brass one.
 */
const REQUEST_TITLE: Partial<Record<SellerDealStatus, string>> = {
  changes_required: "What Dealbid asked for",
  rejected: "Why this was not approved",
  disabled: "Why this was taken off the board",
};

/**
 * The operator's outstanding note (canon APP-020, decision `D-016`).
 *
 * This is the sentence the desk used to withhold: the status said changes were requested and
 * the request itself was nowhere on screen. It sits directly under the meaning it explains
 * and above everything else on the card, because a founder's first question about a deal in
 * this state is what to do about it — not what it costs or what badges it carries.
 *
 * `null` is an ordinary state, not a failure. A deal moderated before core recorded these has
 * no note (`D-016` § 5 refuses to backfill), and an approved deal has nothing outstanding.
 * Either way the card renders exactly as it did before.
 */
function ReviewRequest({ deal }: { deal: SellerDeal }) {
  const titleId = useId();
  const title = REQUEST_TITLE[deal.status];
  if (deal.outstandingNote === null || title === undefined) return null;

  return (
    <section className={styles.request} aria-labelledby={titleId}>
      <h4 className={styles.requestTitle} id={titleId}>
        {title}
      </h4>
      {/* Rendered as text. This is operator-written prose arriving over the network, and it
          is never markup and never a link target. */}
      <p className={styles.requestNote}>{deal.outstandingNote}</p>
    </section>
  );
}

const DECISION_LABEL: Record<SellerDecision["action"], string> = {
  approve: "Approved",
  request_changes: "Changes requested",
  reject: "Not approved",
  disable: "Taken off the board",
};

/**
 * Every decision recorded about this deal (canon APP-020, decision `D-016`).
 *
 * Deliberately secondary to {@link ReviewRequest} and behind a disclosure: a founder's first
 * question is "what do I do now", not "what happened in March". It earns its place anyway,
 * because it is what stops an absent outstanding note from reading as though the record had
 * been thrown away — an approved deal shows nothing outstanding and its whole review is still
 * here.
 *
 * Loaded on first open rather than with the desk. The list read carries the outstanding note
 * and no history (`contracts/API.md` § Seller decision history), so a history for every deal
 * on the dashboard would be a payload nothing on screen was showing.
 *
 * No entry names an operator. Core does not send one and `D-016` § 4 keeps it out
 * permanently, so there is nothing here to omit and nothing to infer.
 */
function ReviewHistory({
  deal,
  loadDealDetail,
}: {
  deal: SellerDeal;
  loadDealDetail: DealEditorProps["loadDetail"];
}) {
  const [state, setState] = useState<
    | { phase: "idle" }
    | { phase: "loading" }
    | { phase: "ready"; decisions: SellerDecision[] }
    | { phase: "failed" }
  >({ phase: "idle" });

  const open = useCallback(() => {
    setState((current) => {
      if (current.phase !== "idle" && current.phase !== "failed") return current;
      void loadDealDetail(deal.id).then(
        (detail) => {
          setState({ phase: "ready", decisions: detail.decisions });
        },
        () => {
          // Said plainly rather than shown as an empty history, which would be this app
          // asserting that Dealbid had decided nothing.
          setState({ phase: "failed" });
        },
      );
      return { phase: "loading" };
    });
  }, [deal.id, loadDealDetail]);

  return (
    <details
      className={styles.history}
      onToggle={(event) => {
        if (event.currentTarget.open) open();
      }}
    >
      <summary className={styles.historySummary}>Review history</summary>
      {state.phase === "loading" ? <Skeleton width="100%" height="2rem" /> : null}
      {state.phase === "failed" ? (
        <p className={styles.historyNote}>
          This deal&rsquo;s review history could not be loaded. Nothing has changed about the
          deal itself.
        </p>
      ) : null}
      {state.phase === "ready" && state.decisions.length === 0 ? (
        <p className={styles.historyNote}>
          Dealbid has not recorded a decision on this deal yet.
        </p>
      ) : null}
      {state.phase === "ready" && state.decisions.length > 0 ? (
        <ol className={styles.historyList}>
          {state.decisions.map((decision) => (
            <li className={styles.historyItem} key={`${decision.decidedAt}-${decision.action}`}>
              <p className={styles.historyHead}>
                <span className={styles.historyAction}>{DECISION_LABEL[decision.action]}</span>
                {formatInstantDate(decision.decidedAt) === null ? null : (
                  <span className={`${styles.historyWhen} figure`}>
                    {formatInstantDate(decision.decidedAt)}
                  </span>
                )}
              </p>
              {decision.note === null ? null : (
                <p className={styles.historyNote}>{decision.note}</p>
              )}
            </li>
          ))}
        </ol>
      ) : null}
    </details>
  );
}

/**
 * What an accepted edit did, in core's own terms (canon APP-005).
 *
 * The one thing it must never omit is a deal that has gone back to review. Core reports
 * that as `returnedToReview`, and a founder who is not told here would discover it by
 * noticing their deal had left the board — which is how a founder loses a day of exposure
 * without knowing why.
 */
function SaveConfirmation({ saved }: { saved: SavedDeal }) {
  const status = STATUS_COPY[saved.deal.status];
  const changed = joinFieldLabels(saved.edit.changedFields);

  return (
    <div className={styles.saved} role="status">
      {saved.edit.returnedToReview ? (
        <>
          <p className={styles.savedTitle}>Saved — this deal has gone back to review.</p>
          <p>
            You changed {changed}, which is part of what Dealbid verified. It has left the
            public board and cannot bid for a Top 10 position until Dealbid approves it
            again. A standing bid keeps its reserved credit until tomorrow&rsquo;s board is
            settled.
          </p>
        </>
      ) : saved.edit.changedFields.length === 0 ? (
        <p className={styles.savedTitle}>
          Saved. Nothing differed from what Dealbid already had, so the deal is unchanged.
        </p>
      ) : (
        <>
          <p className={styles.savedTitle}>Saved — {changed} updated.</p>
          <p>This deal stays where it was in review: {status.label.toLocaleLowerCase()}.</p>
        </>
      )}
    </div>
  );
}

function DealCard({
  deal,
  loadDealDetail,
  saveDeal,
  onDealSaved,
}: {
  deal: SellerDeal;
  loadDealDetail: DealEditorProps["loadDetail"];
  saveDeal: DealEditorProps["saveDeal"];
  onDealSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<SavedDeal | null>(null);
  /**
   * The deal as core answered the edit, shown until the desk's own re-read lands.
   *
   * Without it the card would go on stating "Approved" beside a confirmation saying the
   * deal has gone back to review — one screen contradicting itself for as long as the
   * refresh takes, or indefinitely if that background read fails. Both halves are core's;
   * this only decides which of the two is newer.
   */
  const [edited, setEdited] = useState<SellerDeal | null>(null);
  const [seen, setSeen] = useState(deal);
  const editButton = useRef<HTMLButtonElement>(null);
  /**
   * Set when the editor is closed, so focus is restored on the render that brings the
   * button back. The control is unmounted while the editor is open — its ref is null at the
   * moment the founder saves or cancels — so the focus call has to wait for it to exist.
   */
  const restoreFocus = useRef(false);

  useEffect(() => {
    if (editing || !restoreFocus.current) return;
    restoreFocus.current = false;
    editButton.current?.focus();
  }, [editing]);

  // The desk's re-read has landed with a fresh object: it is now the newer of the two, and
  // the edit response stops standing in for it.
  if (seen !== deal) {
    setSeen(deal);
    setEdited(null);
  }

  const shown = edited ?? deal;
  const status = STATUS_COPY[shown.status];
  const dealUrl = safeExternalUrl(shown.dealUrl);
  const host = displayHost(shown.productUrl) ?? displayHost(shown.dealUrl);

  // Focus returns to the control the founder left, whether they saved or cancelled. A
  // keyboard user who is dropped at the top of the document instead has to find their way
  // back down to the deal they were working on.
  const closeEditor = useCallback(() => {
    restoreFocus.current = true;
    setEditing(false);
  }, []);

  const openEditor = useCallback(() => {
    setEditing(true);
  }, []);

  const onSaved = useCallback(
    (result: SavedDeal) => {
      setSaved(result);
      setEdited(result.deal);
      closeEditor();
      // Core is the authority on every other panel too — an edit can take a deal off
      // tomorrow's auction — so the desk re-reads rather than this card patching itself.
      onDealSaved();
    },
    [closeEditor, onDealSaved],
  );

  return (
    <Card as="article" className={styles.deal}>
      <div className={styles.head}>
        <div className={styles.names}>
          <h3 className={styles.product}>{shown.productName}</h3>
          {host === null ? null : <p className={styles.host}>{host}</p>}
        </div>
        <StatusPill label={status.label} tone={status.tone} description="Review status" />
      </div>

      <p className={styles.meaning}>{status.meaning}</p>

      <ReviewRequest deal={shown} />

      {saved === null ? null : <SaveConfirmation saved={saved} />}

      {shown.shortDescription === null ? null : (
        <p className={styles.description}>{shown.shortDescription}</p>
      )}

      <DealValue deal={shown} />
      <DealBadges deal={shown} />

      <dl className={styles.facts}>
        {shown.couponCode === null ? null : (
          <div className={styles.fact}>
            <dt>Code</dt>
            <dd className="figure">{shown.couponCode}</dd>
          </div>
        )}
        {shown.limitations === null ? null : (
          <div className={styles.fact}>
            <dt>Limitations</dt>
            <dd>{shown.limitations}</dd>
          </div>
        )}
        <ReviewFact label="Submitted" iso={shown.submittedAt} />
        <ReviewFact label="Approved" iso={shown.approvedAt} />
        <ReviewFact label="Verified" iso={shown.verifiedAt} />
      </dl>

      {dealUrl === null ? (
        // Canon docs/SECURITY.md § URLs: only http/https destinations are ever linked.
        // The deal keeps its information and drops the link rather than shipping one that
        // cannot be trusted.
        <p className={styles.unavailable}>
          This deal&rsquo;s destination link is not one Dealbid can open.
        </p>
      ) : (
        <p className={styles.link}>
          <a href={dealUrl} target="_blank" rel="noopener noreferrer">
            Open the deal page
            <VisuallyHidden> for {shown.productName} (opens in a new tab)</VisuallyHidden>
          </a>
        </p>
      )}

      {editing ? (
        <DealEditor
          // Core's own id for this deal, from this session's read — never a value from the
          // address bar or from anything a page could plant (canon APP-001). Core binds
          // ownership inside its own statement and answers 404 for anyone else's deal.
          deal={shown}
          loadDetail={loadDealDetail}
          saveDeal={saveDeal}
          onSaved={onSaved}
          onClose={closeEditor}
        />
      ) : (
        <p className={styles.edit}>
          {/*
           * Offered on every deal, including a rejected or disabled one. Whether this deal
           * may be edited is core's decision and travels in `editPolicy`; the editor asks
           * and shows the answer. Deciding it here would be a second copy of canon § 15,
           * and a founder whose deal was taken down is better served by a sentence saying
           * so than by a control that silently is not there.
           */}
          <Button variant="secondary" onClick={openEditor} ref={editButton}>
            <span aria-hidden="true">Edit deal</span>
            {/* The whole accessible name in one node — see DealEditor's actions. */}
            <VisuallyHidden>Edit deal — {shown.productName}</VisuallyHidden>
          </Button>
        </p>
      )}

      <ReviewHistory deal={shown} loadDealDetail={loadDealDetail} />
    </Card>
  );
}

export function DealsSkeleton() {
  return (
    <Card className={styles.deal}>
      <Skeleton width="12rem" height="1.5rem" />
      <Skeleton width="100%" height="1rem" />
      <Skeleton width="80%" height="1rem" />
    </Card>
  );
}

export interface DealsPanelProps {
  deals: SellerDeal[];
  loadDealDetail: DealEditorProps["loadDetail"];
  saveDeal: DealEditorProps["saveDeal"];
  /** Called after core accepts an edit, so the desk can re-read what it changed. */
  onDealSaved: () => void;
  loadTerms?: SubmitDealProps["loadTerms"];
  submitDeal?: SubmitDealProps["submitDeal"];
  /** Called after core accepts a submission, so the desk can re-read its deals. */
  onDealSubmitted: () => void;
  /**
   * Whether the submission form is open (`APP-016`).
   *
   * Owned by `DeskScreen` rather than by this panel, because `Submit a deal` now sits in the
   * desk's own bar and can be pressed from any of the four views — including the three this
   * panel is not rendered on. State that a control outside the panel toggles cannot live
   * inside it.
   */
  submitOpen: boolean;
  onOpenSubmit: () => void;
  onCloseSubmit: () => void;
}

/**
 * What a founder is told once a deal is filed (canon APP-009).
 *
 * It reports the two things they cannot see for themselves: that nothing is public yet, and
 * which wording core actually recorded them accepting. The deal's own card is directly below
 * carrying its review status, so this does not repeat it.
 */
function SubmitConfirmation({ created }: { created: SubmittedDeal }) {
  return (
    <div className={styles.saved} role="status">
      <p className={styles.savedTitle}>
        {created.deal.productName} is with Dealbid&rsquo;s reviewers.
      </p>
      <p>
        Nothing is public yet. A deal reaches the board only after a person has checked the
        product, your connection to it, the links and the advertised value — and it can bid
        for a Top 10 position only once it is approved.
      </p>
      <p>
        Dealbid recorded your acceptance of the submission terms, version{" "}
        <span className="figure">{created.terms.version}</span>, with this deal.
      </p>
    </div>
  );
}

export function DealsPanel({
  deals,
  loadDealDetail,
  saveDeal,
  onDealSaved,
  loadTerms,
  submitDeal,
  onDealSubmitted,
  submitOpen,
  onOpenSubmit,
  onCloseSubmit,
}: DealsPanelProps) {
  const submitting = submitOpen;
  const [created, setCreated] = useState<SubmittedDeal | null>(null);
  /**
   * The created deal, shown until the desk's own re-read carries it.
   *
   * Separate from `created` above, which is the confirmation: the confirmation is what
   * happened and stays on screen, while this only stands in for a row the list does not have
   * yet. Collapsing the two would take the sentence away the moment the re-read landed —
   * `DealCard` keeps `saved` and `edited` apart for the same reason.
   */
  const [shadow, setShadow] = useState<SellerDeal | null>(null);
  const [seen, setSeen] = useState(deals);
  const submitButton = useRef<HTMLButtonElement>(null);
  /**
   * Set when the form is closed, so focus is restored on the render that brings the control
   * back. The button is unmounted while the form is open — its ref is null at the moment the
   * founder submits or cancels — so the focus call has to wait for it to exist.
   */
  const restoreFocus = useRef(false);

  useEffect(() => {
    if (submitting || !restoreFocus.current) return;
    restoreFocus.current = false;
    submitButton.current?.focus();
  }, [submitting]);

  // The desk's re-read has landed with a fresh array: it is now the newer of the two, and
  // the submission response stops standing in for it. Same discipline as `DealCard`'s
  // `edited`.
  if (seen !== deals) {
    setSeen(deals);
    setShadow(null);
  }

  const closeForm = useCallback(() => {
    restoreFocus.current = true;
    onCloseSubmit();
  }, [onCloseSubmit]);

  const openForm = useCallback(() => {
    onOpenSubmit();
  }, [onOpenSubmit]);

  const onSubmitted = useCallback(
    (result: SubmittedDeal) => {
      setCreated(result);
      setShadow(result.deal);
      closeForm();
      // Core is the authority on the list; this only shows what it answered until the
      // desk's own re-read lands.
      onDealSubmitted();
    },
    [closeForm, onDealSubmitted],
  );

  /**
   * The list, with a just-created deal shown until the desk's re-read carries it.
   *
   * Canon `contracts/API.md` answers a submission with the same projection the list returns,
   * so the created deal is rendered as an ordinary card at its real review status rather
   * than as a special "pending" row. It is filtered by id so it cannot appear twice in the
   * window between the response landing and the re-read completing.
   */
  const shown =
    shadow === null || deals.some((deal) => deal.id === shadow.id) ? deals : [shadow, ...deals];

  const form = (
    <SubmitDeal
      {...(loadTerms === undefined ? {} : { loadTerms })}
      {...(submitDeal === undefined ? {} : { submitDeal })}
      onSubmitted={onSubmitted}
      {...(shown.length === 0 ? {} : { onCancel: closeForm })}
    />
  );

  if (shown.length === 0) {
    /*
     * Not an error, and no longer a signpost to somewhere else. Canon decision `D-008` moved
     * submission behind the session (CORE-024), so this *is* where a deal is created.
     *
     * `APP-016` puts the explanation before the form rather than dropping a founder straight
     * into eight fields: `contracts/design/app-desk/` artboard 1 draws the empty state as a
     * statement plus one action, and that artboard is the first screen every founder ever
     * sees. Pressing the action — or `Submit a deal` in the desk's bar, which sets the same
     * state — opens the form in place.
     */
    if (submitting) return <Card className={styles.first}>{form}</Card>;

    return (
      <Card className={styles.first}>
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>No deals yet</h3>
          <p className={styles.emptyBody}>
            Dealbid lists only deals it has reviewed and approved. Submitting a deal starts
            that review — approval is the gate to bidding, to an Honorable Mention and to
            everything else on this desk.
          </p>
          <p>
            <Button onClick={openForm} ref={submitButton}>
              Submit your first deal
            </Button>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className={styles.panel}>
      {created === null ? null : <SubmitConfirmation created={created} />}

      <ul className={styles.list} role="list">
        {shown.map((deal) => (
          <li key={deal.id}>
            <DealCard
              deal={deal}
              loadDealDetail={loadDealDetail}
              saveDeal={saveDeal}
              onDealSaved={onDealSaved}
            />
          </li>
        ))}
      </ul>

      {submitting ? (
        <Card className={styles.another}>{form}</Card>
      ) : (
        <p className={styles.add}>
          <Button variant="secondary" onClick={openForm} ref={submitButton}>
            Submit another deal
          </Button>
        </p>
      )}
    </div>
  );
}
